/**
 * JobStatusActionButton - Primary row CTA for the work-order /
 * inspection list pages.
 *
 * Resolves the button label and click semantics from the record's
 * status via getJobAction():
 *	 - Start:  triggers onStart() (typically a PATCH that flips the
 *			   record into 'in_progress') then onActive() to jump
 *			   straight into the workflow / actionable surface.
 *	 - Resume: triggers onActive() — same destination as a successful
 *			   Start so the user lands on the actionable surface, not
 *			   the read-only details tab.
 *	 - View:	  triggers onView() — read-only detail page for terminal
 *			   records (completed / cancelled / closed).
 *
 * The visual emphasis varies by kind so the table communicates intent
 * at a glance:
 *	 - Start  → solid brand-primary fill (high emphasis)
 *	 - Resume → outlined brand-primary (medium emphasis)
 *	 - View	  → ghost link (low emphasis)
 *
 * onStart is async to support optimistic UI / spinner states. While
 * the start request is in flight the button shows "Starting…" and
 * disables itself to prevent double submission.
 */
import React, { useState } from 'react';
import { getJobAction } from '../../../utils/jobAction';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface JobStatusActionButtonProps {
	/** The record's current status. Determines which CTA renders. */
	status: string | null | undefined;
	/** Called when the user clicks Start. Should transition status server-side. */
	onStart: () => Promise<void> | void;
	/**
	 * Navigation handler for the active states (Start success + Resume).
	 * Should land the user on the actionable surface — e.g. the work
	 * order workflow overlay or the inspection's actionable tab.
	 */
	onActive: () => void;
	/**
	 * Navigation handler for the View kind. Lands on the read-only
	 * detail page. Falls back to onActive if not provided so callers
	 * that don't distinguish read-only vs. actionable still work.
	 */
	onView?: () => void;
	/** Optional aria-label override (e.g. "Start work order WO-1234"). */
	ariaLabel?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const JobStatusActionButton: React.FC<JobStatusActionButtonProps> = ({
	status,
	onStart,
	onActive,
	onView,
	ariaLabel,
}) => {
	const action = getJobAction(status);
	/* Only Start needs a busy state — Resume/View are pure navigation. */
	const [isStarting, setIsStarting] = useState(false);

	/* Each kind gets its own utility classes so the visual emphasis
	   matches the intent: Start is the call-to-action, Resume is the
	   continuation, View is the read-only quiet variant.
	   Start + Resume go full-width below the tablet breakpoint (the
	   stacked-card layouts) for an easy thumb target, and share a
	   min-width from tablet up so the table rows line up vertically
	   regardless of which CTA each row renders — and so the
	   "Starting…" busy label doesn't widen the row mid-click. */
	const buttonClass = (() => {
		const base = 'inline-flex items-center justify-center px-12px py-8px tablet:py-6px text-12px font-sans font-semibold rounded-md transition-colors min-h-44px desktop:min-h-32px focus:outline-none focus:ring-2 focus:ring-offset-1';
		const fillCtaWidth = 'w-full tablet:w-auto min-w-[80px]';
		if (action.kind === 'start') {
			return `${base} ${fillCtaWidth} bg-brand-primary text-white hover:bg-brand-primary-hover focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed`;
		}
		if (action.kind === 'resume') {
			return `${base} ${fillCtaWidth} border border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white focus:ring-brand-primary`;
		}
		/* view — link-style, intentionally sized to its content */
		return `${base} text-brand-text-muted hover:text-brand-primary hover:underline`;
	})();

	const handleClick = async (e: React.MouseEvent) => {
		/* Stop row-level navigation handlers (if any) — the action
		   button's intent is explicit and shouldn't double-fire. */
		e.stopPropagation();

		if (action.kind === 'start') {
			setIsStarting(true);
			try {
				await onStart();
				/* Land the user on the actionable surface (workflow
				   overlay for work orders, actionable tab for
				   inspections) rather than the read-only details. */
				onActive();
			} finally {
				setIsStarting(false);
			}
			return;
		}

		if (action.kind === 'resume') {
			/* Resume jumps to the same actionable surface as a fresh
			   Start so the user can keep working without an extra hop. */
			onActive();
			return;
		}

		/* View — terminal records open the read-only detail page.
		   Fall back to onActive when no read-only handler is supplied. */
		(onView ?? onActive)();
	};

	return (
		<button
			type="button"
			onClick={handleClick}
			disabled={isStarting}
			className={`job-status-action-button job-status-action-button--${action.kind} ${buttonClass}`}
			data-ref="molecule-job-status-action-button"
			aria-label={ariaLabel ?? action.label}
		>
			{isStarting ? 'Starting…' : action.label}
		</button>
	);
};

export default JobStatusActionButton;
